import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type AgentStatusKind =
  | 'never_run'
  | 'idle'
  | 'enabled'
  | 'running'
  | 'paused'
  | 'failed';

export interface AgentStatus {
  kind: AgentStatusKind;
  label: string;
}

export interface AgentWarning {
  text: string;
  severity?: 'warn' | 'error';
}

export interface AgentAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger';
}

export interface AgentLinkAction {
  label: string;
  to: string;
}

export interface AgentActivityItem {
  id: string;
  timeLabel: string;
  description: string;
}

export interface AgentCardProps {
  name: string;
  scopeBadge?: string;
  status: AgentStatus;
  infoLine?: ReactNode;
  warnings?: AgentWarning[];
  primaryAction?: AgentAction;
  menuActions?: (AgentAction | AgentLinkAction)[];
  recentActivity?: AgentActivityItem[];
  onOpenDrawer?: () => void;
}

const STATUS_CLASS: Record<AgentStatusKind, string> = {
  never_run: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  idle: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  enabled: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
  running: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 animate-pulse',
  paused: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  failed: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
};

const ACTION_VARIANT: Record<NonNullable<AgentAction['variant']>, string> = {
  primary: 'bg-indigo-600 text-white hover:bg-indigo-700',
  secondary: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300',
  danger: 'bg-red-600 text-white hover:bg-red-700',
};

export default function AgentCard({
  name,
  scopeBadge,
  status,
  infoLine,
  warnings = [],
  primaryAction,
  menuActions = [],
  recentActivity = [],
  onOpenDrawer,
}: AgentCardProps) {
  const hasActions = primaryAction || menuActions.length > 0;

  return (
    <div
      className={`relative flex flex-col gap-1 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-3 flex-1 min-w-[320px] ${
        onOpenDrawer ? 'cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors' : ''
      }`}
      onClick={onOpenDrawer}
      role={onOpenDrawer ? 'button' : undefined}
      tabIndex={onOpenDrawer ? 0 : undefined}
      onKeyDown={(e) => {
        if (onOpenDrawer && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpenDrawer();
        }
      }}
    >
      {/* Header row: name · scope · status · actions (all one line) */}
      <div className="flex items-center gap-2 min-w-0 flex-wrap">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{name}</span>
        {scopeBadge && (
          <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 whitespace-nowrap">
            {scopeBadge}
          </span>
        )}
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${STATUS_CLASS[status.kind]}`}
        >
          {status.label}
        </span>
        {hasActions && (
          <div
            className="ml-auto flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {primaryAction && (
              <button
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
                className={`px-3 py-1 text-xs font-medium rounded disabled:opacity-50 ${
                  ACTION_VARIANT[primaryAction.variant ?? 'primary']
                }`}
              >
                {primaryAction.label}
              </button>
            )}
            {menuActions.map((m, i) =>
              'to' in m ? (
                <Link
                  key={i}
                  to={m.to}
                  className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  {m.label}
                </Link>
              ) : (
                <button
                  key={i}
                  onClick={m.onClick}
                  disabled={m.disabled}
                  className={`px-2 py-1 text-xs rounded disabled:opacity-50 ${
                    m.variant === 'danger'
                      ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {m.label}
                </button>
              )
            )}
          </div>
        )}
      </div>

      {/* Info line */}
      {infoLine && (
        <div className="text-xs text-gray-600 dark:text-gray-300 truncate">{infoLine}</div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {warnings.map((w, i) => (
            <span
              key={i}
              className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                w.severity === 'error'
                  ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                  : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
              }`}
            >
              ⚠ {w.text}
            </span>
          ))}
        </div>
      )}

      {/* Recent activity — this agent's own timeline */}
      {recentActivity.length > 0 && (
        <ul className="mt-0.5 pl-3 border-l border-gray-100 dark:border-gray-700 space-y-0.5">
          {recentActivity.map((a) => (
            <li
              key={a.id}
              className="flex items-baseline gap-2 text-xs text-gray-500 dark:text-gray-400"
            >
              <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
                {a.timeLabel}
              </span>
              <span className="text-gray-600 dark:text-gray-300 truncate">{a.description}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
